import { useState, useEffect, useMemo } from 'react';
import {
  Globe,
  Search,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Clock,
  ExternalLink,
  Shield,
} from 'lucide-react';
import { getSharePointSites, SharePointSite } from '../../services/api';
import SitePermissionsPanel from './SitePermissionsPanel';
import { format, parseISO } from 'date-fns';
import { SyncCategory, SyncedSharePointSite, SyncedSitePermission, Staleness } from '../../types';
import { formatSyncTime, getStalenessColor } from '../../hooks/useClientSyncData';

interface SharePointSiteListProps {
  clientId: string;
  clientName: string;
  tenantId: string | null;
  syncData?: SyncCategory<SyncedSharePointSite>;
  sitePermissionsData?: SyncCategory<SyncedSitePermission>;
  onRefreshNeeded?: () => void;
}

export default function SharePointSiteList({
  clientId,
  clientName: _clientName,
  tenantId,
  syncData,
  sitePermissionsData,
  onRefreshNeeded: _onRefreshNeeded,
}: SharePointSiteListProps) {
  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [usingCache, setUsingCache] = useState(false);

  // Load from cache if available
  useEffect(() => {
    if (syncData?.data && syncData.data.length > 0 && syncData.status === 'success') {
      // Map cached SyncedSharePointSite to SharePointSite format
      const cachedSites: SharePointSite[] = syncData.data.map(site => ({
        id: site.id,
        displayName: site.displayName,
        webUrl: site.webUrl,
        createdDateTime: site.createdDateTime || undefined,
        lastModifiedDateTime: site.lastModifiedDateTime || undefined,
        isTeamSite: false, // Not stored in cache; not critical for display
      }));
      setSites(cachedSites);
      setHasFetched(true);
      setUsingCache(true);
    }
  }, [syncData]);

  const fetchSites = async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getSharePointSites(clientId);
      if (result.success) {
        setSites(result.sites);
        setUsingCache(false);
      } else {
        setError(result.error || 'Failed to fetch SharePoint sites');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch SharePoint sites');
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  };

  useEffect(() => {
    if (tenantId && !hasFetched) {
      // If no cache data, fetch live
      if (!syncData?.data || syncData.data.length === 0 || syncData.status !== 'success') {
        fetchSites();
      }
    }
  }, [tenantId, hasFetched]);

  const cacheStaleness: Staleness | null = useMemo(() => {
    if (!syncData?.lastSynced) return null;
    const hoursSince = (Date.now() - new Date(syncData.lastSynced).getTime()) / (1000 * 60 * 60);
    if (hoursSince < 4) return 'fresh';
    if (hoursSince < 8) return 'stale';
    return 'very_stale';
  }, [syncData?.lastSynced]);

  const filteredSites = useMemo(() => {
    if (!searchTerm) return sites;
    const term = searchTerm.toLowerCase();
    return sites.filter(
      site =>
        site.displayName.toLowerCase().includes(term) ||
        site.webUrl.toLowerCase().includes(term)
    );
  }, [sites, searchTerm]);

  // No tenant configured
  if (!tenantId) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
        <p className="text-yellow-700 dark:text-yellow-300">
          No Microsoft tenant ID configured. Please link this client to a tenant to manage SharePoint sites.
        </p>
      </div>
    );
  }

  // Loading state
  if (loading && !hasFetched) {
    return (
      <div className="text-center py-12 bg-muted rounded-lg">
        <Loader2 className="h-12 w-12 mx-auto text-primary animate-spin mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Loading SharePoint Sites</h3>
        <p className="text-muted-foreground">Fetching sites from Microsoft Graph API...</p>
      </div>
    );
  }

  // Error state
  if (error && sites.length === 0) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-red-800 dark:text-red-200">Failed to load SharePoint sites</h3>
            <p className="text-red-700 dark:text-red-300 text-sm mt-1">{error}</p>
            <button
              onClick={fetchSites}
              className="text-sm text-red-600 dark:text-red-400 underline mt-2"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with search and refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {sites.length} site{sites.length !== 1 ? 's' : ''} found
          </span>
          {usingCache && syncData?.lastSynced && (
            <span className={`text-xs ${getStalenessColor(cacheStaleness || 'never_synced')}`}>
              Updated {formatSyncTime(syncData.lastSynced)}
            </span>
          )}
          <button
            onClick={fetchSites}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors"
            title="Fetch live data from Microsoft Graph API"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Search */}
        {sites.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search sites..."
              className="pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary w-full sm:w-64"
            />
          </div>
        )}
      </div>

      {/* Site List */}
      {filteredSites.length === 0 ? (
        <div className="text-center py-8 bg-muted rounded-lg">
          {searchTerm ? (
            <>
              <Search className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No sites matching "{searchTerm}"</p>
            </>
          ) : (
            <>
              <Globe className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No SharePoint sites found for this tenant.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSites.map(site => (
            <div key={site.id}>
              <div className="border border-border rounded-lg p-4 bg-card hover:border-primary/30 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-primary flex-shrink-0" />
                      <h4 className="font-medium text-foreground truncate">{site.displayName}</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 truncate">{site.webUrl}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      {site.createdDateTime && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Created {format(parseISO(site.createdDateTime), 'MMM d, yyyy')}
                        </span>
                      )}
                      {site.lastModifiedDateTime && (
                        <span>
                          Modified {format(parseISO(site.lastModifiedDateTime), 'MMM d, yyyy')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a
                      href={site.webUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-muted-foreground hover:text-foreground rounded transition-colors"
                      title="Open in SharePoint"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <button
                      onClick={() => setSelectedSite(selectedSite?.id === site.id ? null : site)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition-colors ${
                        selectedSite?.id === site.id
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-foreground hover:bg-accent'
                      }`}
                    >
                      <Shield className="h-3.5 w-3.5" />
                      {selectedSite?.id === site.id ? 'Hide Permissions' : 'View Permissions'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Inline Permissions Panel */}
              {selectedSite?.id === site.id && (
                <SitePermissionsPanel
                  clientId={clientId}
                  siteUrl={site.webUrl}
                  siteName={site.displayName}
                  onClose={() => setSelectedSite(null)}
                  cachedPermissions={sitePermissionsData?.data?.find(p => p.siteUrl === site.webUrl || p.siteId === site.id) || null}
                  cachedAt={sitePermissionsData?.lastSynced || null}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
